import type { MeetingJoinSettings } from './types';

export type CachedLiveKitJoinCreds = {
  token: string;
  livekitUrl: string;
  /** When set, same-room remints keep the live MeetingRoom mounted. */
  roomName?: string;
  /** ISO timestamp from token mint — drives pre-expiry remint after remount. */
  expiresAt?: string;
};

type LecturerSlotSessionSnapshot = {
  presenceSetupDone: boolean;
  lobbyDone: boolean;
  joinSettings?: MeetingJoinSettings;
};

const LECTURER_SLOT_SESSION_PREFIX = 'flare.lecturerSlotSession.';

/** Survives React remounts / Fast Refresh within the same page session. */
const joinCredsByKey = new Map<string, CachedLiveKitJoinCreds>();
const lecturerSlotSessionById = new Map<string, LecturerSlotSessionSnapshot>();
/** Durable fallback when `sessionStorage` is missing (RN / Jest). */
const durableSlotSessionById = new Map<string, LecturerSlotSessionSnapshot>();

function canUseSessionStorage(): boolean {
  try {
    return typeof sessionStorage !== 'undefined';
  } catch {
    return false;
  }
}

function sessionStorageKey(slotId: string): string {
  return `${LECTURER_SLOT_SESSION_PREFIX}${slotId}`;
}

function readDurableSnapshot(slotId: string): LecturerSlotSessionSnapshot | null {
  if (canUseSessionStorage()) {
    try {
      const raw = sessionStorage.getItem(sessionStorageKey(slotId));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as LecturerSlotSessionSnapshot;
      if (typeof parsed?.presenceSetupDone !== 'boolean' || typeof parsed?.lobbyDone !== 'boolean') {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }
  return durableSlotSessionById.get(slotId) ?? null;
}

function writeDurableSnapshot(slotId: string, snapshot: LecturerSlotSessionSnapshot): void {
  if (canUseSessionStorage()) {
    try {
      sessionStorage.setItem(sessionStorageKey(slotId), JSON.stringify(snapshot));
      return;
    } catch {
      // Private mode / quota — fall through to the in-process durable map.
    }
  }
  durableSlotSessionById.set(slotId, snapshot);
}

function clearDurableSnapshot(slotId: string): void {
  durableSlotSessionById.delete(slotId);
  if (!canUseSessionStorage()) return;
  try {
    sessionStorage.removeItem(sessionStorageKey(slotId));
  } catch {
    // ignore
  }
}

function clearAllDurableSnapshots(): void {
  durableSlotSessionById.clear();
  if (!canUseSessionStorage()) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(LECTURER_SLOT_SESSION_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}

export function readCachedJoinCreds(cacheKey: string): CachedLiveKitJoinCreds | null {
  return joinCredsByKey.get(cacheKey) ?? null;
}

export function writeCachedJoinCreds(cacheKey: string, creds: CachedLiveKitJoinCreds): void {
  joinCredsByKey.set(cacheKey, creds);
}

export function clearCachedJoinCreds(cacheKey: string): void {
  joinCredsByKey.delete(cacheKey);
}

export function readLecturerSlotSession(slotId: string): LecturerSlotSessionSnapshot | null {
  const memory = lecturerSlotSessionById.get(slotId);
  if (memory) return memory;
  const stored = readDurableSnapshot(slotId);
  if (stored) {
    lecturerSlotSessionById.set(slotId, stored);
  }
  return stored;
}

export function writeLecturerSlotSession(
  slotId: string,
  patch: Partial<LecturerSlotSessionSnapshot>,
): void {
  const next: LecturerSlotSessionSnapshot = {
    presenceSetupDone: false,
    lobbyDone: false,
    ...lecturerSlotSessionById.get(slotId),
    ...readDurableSnapshot(slotId),
    ...patch,
  };
  lecturerSlotSessionById.set(slotId, next);
  writeDurableSnapshot(slotId, next);
}

export function clearLecturerSlotSession(slotId: string): void {
  lecturerSlotSessionById.delete(slotId);
  clearDurableSnapshot(slotId);
}

/** Test-only: drop in-memory maps so a refresh can be simulated against sessionStorage. */
export function clearMeetingSessionMemoryForTests(): void {
  joinCredsByKey.clear();
  lecturerSlotSessionById.clear();
}

/** Test-only reset — not used in production paths. */
export function clearMeetingSessionPersistenceForTests(): void {
  clearMeetingSessionMemoryForTests();
  clearAllDurableSnapshots();
}
